import { LazyQueryHookOptions, QueryHookOptions } from '@apollo/client';

import {
  BannerConfigurationEdge,
  BannerConfigurationFragment,
  DisplayCategoriesQuery,
  DisplayCategoriesQueryVariables,
  DisplayCategoryFilter,
  DisplayCategoryFragment,
  DisplayCategoryQuery,
  DisplayCategoryQueryVariables,
  useDisplayCategoriesLazyQuery,
  useDisplayCategoriesQuery,
  useDisplayCategoryQuery,
} from '@occc/graphql';
import { PaginationInfo, usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';
import config from '@occc/config';
import { useChainId } from '@occc-app/Chain/hooks';

import { useLazyQuery } from '../utils';

export const useGetDisplayCategoryById = (idEquals: string) => {
  const response = useDisplayCategoryQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: {
        idEquals,
      },
      skipBanners: !config.features.externalBannersEnabled,
    },
  });

  const transformDataToBannerConfigurationsArray = (
    data: DisplayCategoryQuery,
  ): BannerConfigurationFragment[] | null => {
    return data?.bannerConfigurations?.edges
      ? data?.bannerConfigurations?.edges?.map((edge: BannerConfigurationEdge) => edge.node)
      : null;
  };

  const displayCategory = response.data?.displayCategories?.edges[0]?.node;

  return {
    ...response,
    data: {
      displayCategory,
      bannerConfigurations: response.data
        ? transformDataToBannerConfigurationsArray(response.data)
        : null,
    },
  };
};

export const useGetDisplayCategoriesByIds = (idEquals: string[]) => {
  const response = useDisplayCategoryQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !idEquals.length,
    variables: {
      filter: {
        uuidContains: idEquals,
      },
      skipBanners: !config.features.externalBannersEnabled,
    },
  });

  const data = response.data?.displayCategories?.edges.map((edge) => edge.node);

  return {
    ...response,
    data,
  };
};

type Options = QueryHookOptions<DisplayCategoryFragment[], DisplayCategoryQueryVariables>;

interface GetDisplayCategoriesProps {
  filter?: DisplayCategoryFilter;
  pageSize?: number;
  baseOptions?: Options;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
}

const transform = (data: DisplayCategoryQuery) => {
  const connection = data.displayCategories;

  if (!connection) {
    return [];
  }

  const nodes = connection.edges.map((edge) => edge.node);
  return nodes;
};

export const useGetDisplayCategories = ({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetDisplayCategoriesProps = {}) => {
  const chainId = useChainId();

  const response = usePaginatedQuery(
    useDisplayCategoriesQuery,
    'displayCategories.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter: {
          chainIdEquals: chainId,
          ...filter,
        },
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions?.onCompleted) {
          baseOptions?.onCompleted(transform(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data ? transform(response.data) : response.data,
  };
};

export const useGetDisplayCategoriesLazy = (
  baseOptions?: LazyQueryHookOptions<DisplayCategoriesQuery, DisplayCategoriesQueryVariables>,
) => {
  const chainId = useChainId();
  const [executeQuery, response] = useLazyQuery(useDisplayCategoriesLazyQuery, baseOptions);

  return {
    executeQuery: (
      options?: LazyQueryHookOptions<DisplayCategoriesQuery, DisplayCategoriesQueryVariables>,
    ) =>
      executeQuery({
        ...options,
        variables: {
          ...options?.variables,
          filter: {
            ...options?.variables?.filter,
            chainIdEquals: chainId,
          },
        },
      }),
    response: {
      ...response,
      data: response.data ? transform(response.data) : response.data,
    },
  };
};
